import React, { useContext, useMemo, useState } from 'react';
import {
  Alert,
  FlatList,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { Button, IconButton, Text, TextInput } from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import JobsContext from '../../context/JobsContext';
import { ResumeDocument } from '../../types';
import { Palette, radius, shadow, spacing, useThemePalette } from '../../theme';
import { track } from '../../lib/analytics';
import ModalSheet from '../ModalSheet';
import LoggedOutBanner from '../LoggedOutBanner';
import ResumeEditor from './ResumeEditor';

const formatDate = (iso: string, locale: string): string => {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat(locale || undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(d);
  } catch {
    return iso;
  }
};

const ResumeListScreen: React.FC = () => {
  const { t, i18n } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);
  const { resumes, addResume, deleteResume, duplicateResume } = useContext(JobsContext);

  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState<ResumeDocument | null>(null);
  const [actionTarget, setActionTarget] = useState<ResumeDocument | null>(null);

  const openCreate = () => {
    setNewName('');
    setCreating(true);
  };

  const confirmCreate = () => {
    const name = newName.trim();
    if (!name) return;
    const created = addResume(name, 'modern');
    track('resume_create', { template: created.template });
    setCreating(false);
    setEditing(created);
  };

  const startEdit = (r: ResumeDocument) => {
    setActionTarget(null);
    // Re-resolve from latest state through context-derived lookup
    setEditing(r);
  };

  const confirmDelete = (r: ResumeDocument) => {
    setActionTarget(null);
    Alert.alert(
      t('resume.actions.deleteConfirmTitle'),
      t('resume.actions.deleteConfirmMessage', { name: r.name }),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('resume.actions.delete'),
          style: 'destructive',
          onPress: () => {
            deleteResume(r.id);
            track('resume_delete', { id: r.id });
          },
        },
      ]
    );
  };

  const onDuplicate = (r: ResumeDocument) => {
    setActionTarget(null);
    const created = duplicateResume(r.id);
    if (created) track('resume_duplicate', { id: r.id });
  };

  // Look the resume up from latest context resumes when editing so updates
  // and renames propagate without forcing a re-pick.
  const editingResume = editing
    ? resumes.find((r) => r.id === editing.id) ?? editing
    : null;

  const renderItem = ({ item }: { item: ResumeDocument }) => (
    <TouchableOpacity
      style={styles.card}
      activeOpacity={0.85}
      onPress={() => startEdit(item)}
    >
      <View style={styles.cardBody}>
        <View style={styles.cardHead}>
          <Text style={styles.cardName} numberOfLines={1}>
            {item.name}
          </Text>
          <IconButton
            icon="dots-vertical"
            size={20}
            onPress={() => setActionTarget(item)}
            iconColor={palette.textSecondary}
            accessibilityLabel={t('resume.actions.more')}
          />
        </View>
        <View style={styles.cardMeta}>
          <View style={styles.templateChip}>
            <MaterialCommunityIcons
              name="palette-outline"
              size={14}
              color={palette.appliedText}
            />
            <Text style={styles.templateChipText}>
              {t(`resume.templates.${item.template}`)}
            </Text>
          </View>
          <Text style={styles.cardDate}>
            {t('resume.lastModified')} · {formatDate(item.updatedAt, i18n.language)}
          </Text>
        </View>
      </View>
    </TouchableOpacity>
  );

  const empty = (
    <View style={styles.empty}>
      <MaterialCommunityIcons
        name="file-document-edit-outline"
        size={64}
        color={palette.textMuted}
      />
      <Text style={styles.emptyTitle}>{t('resume.empty.title')}</Text>
      <Text style={styles.emptySubtitle}>{t('resume.empty.subtitle')}</Text>
      <Button
        mode="contained"
        icon="plus"
        onPress={openCreate}
        buttonColor={palette.primary}
        textColor="#0F1115"
        style={styles.emptyCta}
      >
        {t('resume.empty.cta')}
      </Button>
    </View>
  );

  return (
    <SafeAreaView style={styles.safeArea} edges={['top']}>
      <View style={styles.header}>
        <Text style={styles.title}>{t('resume.title')}</Text>
        {resumes.length > 0 && (
          <Button
            mode="contained"
            icon="plus"
            onPress={openCreate}
            buttonColor={palette.primary}
            textColor="#0F1115"
            compact
          >
            {t('resume.newResume')}
          </Button>
        )}
      </View>

      <LoggedOutBanner />

      <FlatList
        data={resumes}
        keyExtractor={(r) => r.id}
        renderItem={renderItem}
        contentContainerStyle={
          resumes.length === 0 ? styles.emptyContainer : styles.listContent
        }
        ListEmptyComponent={empty}
      />

      <ModalSheet
        visible={creating}
        onDismiss={() => setCreating(false)}
        cardStyle={styles.modalCard}
      >
        <View style={styles.modalBody}>
          <Text style={styles.modalTitle}>{t('resume.newResumePrompt')}</Text>
          <TextInput
            mode="outlined"
            label={t('resume.namePlaceholder')}
            value={newName}
            onChangeText={setNewName}
            autoFocus
            onSubmitEditing={confirmCreate}
            returnKeyType="done"
          />
          <View style={styles.modalFooter}>
            <Button
              mode="text"
              onPress={() => setCreating(false)}
              textColor={palette.textPrimary}
            >
              {t('common.cancel')}
            </Button>
            <Button
              mode="contained"
              onPress={confirmCreate}
              disabled={!newName.trim()}
              buttonColor={palette.primary}
              textColor="#0F1115"
            >
              {t('resume.buttons.create')}
            </Button>
          </View>
        </View>
      </ModalSheet>

      <ModalSheet
        visible={actionTarget !== null}
        onDismiss={() => setActionTarget(null)}
        cardStyle={styles.modalCard}
      >
        {actionTarget && (
          <View style={styles.modalBody}>
            <Text style={styles.modalTitle} numberOfLines={1}>
              {actionTarget.name}
            </Text>
            <TouchableOpacity
              style={styles.actionRow}
              onPress={() => startEdit(actionTarget)}
            >
              <MaterialCommunityIcons
                name="pencil-outline"
                size={20}
                color={palette.textPrimary}
              />
              <Text style={styles.actionLabel}>{t('resume.actions.edit')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.actionRow}
              onPress={() => onDuplicate(actionTarget)}
            >
              <MaterialCommunityIcons
                name="content-copy"
                size={20}
                color={palette.textPrimary}
              />
              <Text style={styles.actionLabel}>{t('resume.actions.duplicate')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.actionRow}
              onPress={() => confirmDelete(actionTarget)}
            >
              <MaterialCommunityIcons
                name="delete-outline"
                size={20}
                color={palette.danger}
              />
              <Text style={[styles.actionLabel, { color: palette.danger }]}>
                {t('resume.actions.delete')}
              </Text>
            </TouchableOpacity>
          </View>
        )}
      </ModalSheet>

      {editingResume && (
        <ResumeEditor
          visible={editingResume !== null}
          initial={editingResume}
          onDismiss={() => setEditing(null)}
        />
      )}
    </SafeAreaView>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    safeArea: { flex: 1, backgroundColor: p.canvas },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: spacing.lg,
      paddingTop: spacing.lg,
      paddingBottom: spacing.sm,
    },
    title: { fontSize: 22, fontWeight: '700', color: p.textPrimary },
    listContent: { paddingHorizontal: spacing.lg, paddingBottom: spacing.lg },
    emptyContainer: { flexGrow: 1 },
    card: {
      backgroundColor: p.surface,
      borderRadius: radius.md,
      marginBottom: spacing.sm,
      borderWidth: 1,
      borderColor: p.border,
      ...shadow.card,
    },
    cardBody: { padding: spacing.md, paddingRight: spacing.xs },
    cardHead: { flexDirection: 'row', alignItems: 'center' },
    cardName: { flex: 1, fontSize: 16, fontWeight: '700', color: p.textPrimary },
    cardMeta: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      marginTop: 2,
      paddingRight: spacing.md,
    },
    templateChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: spacing.sm,
      paddingVertical: 2,
      borderRadius: radius.pill,
      backgroundColor: p.appliedBg,
    },
    templateChipText: { fontSize: 11, fontWeight: '700', color: p.appliedText },
    cardDate: { fontSize: 11, color: p.textMuted, flexShrink: 1 },
    empty: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: spacing.xl,
      gap: spacing.sm,
    },
    emptyTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: p.textPrimary,
      marginTop: spacing.sm,
    },
    emptySubtitle: {
      fontSize: 14,
      color: p.textSecondary,
      textAlign: 'center',
      lineHeight: 20,
    },
    emptyCta: { borderRadius: radius.pill, marginTop: spacing.md },
    modalCard: { backgroundColor: p.surface, borderRadius: radius.lg },
    modalBody: { padding: spacing.lg, gap: spacing.md },
    modalTitle: { fontSize: 18, fontWeight: '700', color: p.textPrimary },
    modalFooter: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      gap: spacing.sm,
    },
    actionRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.md,
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.sm,
      borderRadius: radius.md,
    },
    actionLabel: { fontSize: 15, color: p.textPrimary, fontWeight: '500' },
  });

export default ResumeListScreen;
